import { SUB_ROWS, SUB_PER_PLOT } from '../../../rules/subplotGrid'
import {
    SIDES, plotTileBox, LANE_RADIUS, LANE_OPP_OPACITY, LANE_STROKE, LANE_STROKE_W, LANE_STROKE_ARMED,
    LANE_STROKE_HOVER, LANE_FLASH_FILL, LANE_FLASH_STROKE,
    CARET_HALF_W, CARET_H, CARET_LIFT_MAX, CARET_FILL, CARET_SHADOW_DY,
    CHEVRON_STEP, CHEVRON_OPACITY, CHEVRON_W, CHEVRON_HALO_W,
} from './geometry'

// ─── Hex board — airspace lanes + attack carets ───────────────────────────────
//
// Corridor capacity reads from LANES: one capsule of airspace per corridor
// per half, a drone tile wide, running from the first hosting slot's centre
// to the last's. Its length IS the capacity; each drone is one slot tall, so
// the empty remainder past the last drone is the free space. The lane is a
// faint, cool, feathered haze over the land that thickens toward its far end
// so the pill's cap carries the boundary, with a thin stroke for a defined
// edge even at full column length. (Per-slot pad rings, a dashed threshold
// bar and a greyed no-fly zone were all tried and rejected.)
//
// ATTACK lives on the lane: a corridor that may attack grows a red CARET out
// of its lane's band-end cap — the pill's own point — with two fading
// chevrons stacked above it as the slide-up cue. Arming turns the lane
// attack-red and drops the chevrons. The caret is also the desktop click
// target and the visual follow for the drag; the gesture itself is handled
// by HexBoard.
//
// buildLanes() returns { lanes, carets, caretShadows } — three SVG layers so
// HexBoard can stack them correctly (shadows under the pills, carets on top).

export function laneBox(side, c, cap) {
    const first = plotTileBox(side, c, 0)
    const last  = plotTileBox(side, c, cap - 1)
    const w = 2 * LANE_RADIUS
    return {
        x: first.cx - LANE_RADIUS,
        y: Math.min(first.cy, last.cy) - LANE_RADIUS,
        w,
        h: Math.abs(last.cy - first.cy) + w,
        cx: first.cx,
    }
}

// A corridor whose one move this round is SPENT wears a faint mark on the
// front (band-end) cap of its lane, on the side the drone left by: a quarter
// of the cap's arc dashed in the moved drone's own yellow-and-black bands.
// The remaining drones can't leave; the mark says where the move went.
const SPENT_DASH = 3          // the moved tile's stripe pitch: 3px yellow, 3px black
const SPENT_W = 2
const SPENT_OPACITY = 0.45
function spentMoveMark(c, x, y, cx, dir) {
    const r = LANE_RADIUS
    const arcCy = y + r
    const sx = dir === 'left' ? cx - r : cx + r
    // From the cap's apex round to the side point, on the departure side.
    const d = `M ${cx} ${y} A ${r} ${r} 0 0 ${dir === 'left' ? 0 : 1} ${sx} ${arcCy}`
    return (
        <g key={`spent-${c}`} data-spent-move={`${c}-${dir}`} opacity={SPENT_OPACITY} style={{ pointerEvents: 'none' }}>
            <path d={d} fill="none" stroke="#000000" strokeWidth={SPENT_W} strokeDasharray={`${SPENT_DASH} ${SPENT_DASH}`} />
            <path d={d} fill="none" stroke="#f0e040" strokeWidth={SPENT_W} strokeDasharray={`${SPENT_DASH} ${SPENT_DASH}`} strokeDashoffset={SPENT_DASH} />
        </g>
    )
}

// hoverCol: the self corridor a dragged drone is hovering into (subtle amber
// edge). flash: { col, key } — the corridor a move just landed in; keyed so
// each landing replays the one-shot amber blink. spentMoves: Map col →
// 'left' | 'right', the self corridors whose move went out this round.
// aimingCols: the corridors whose crosshairs are hunting — their opp pill
// clears away (HexBoard.css [data-aiming]) so the aim reads on bare land.
export function buildLanes({ hostingCaps, armedCols, eligibleCols, caretLift, interactive, caretHandlers, hoverCol = null, flash = null, spentMoves = null, aimingCols = null }) {
    const lanes = [], carets = [], caretShadows = []
    const maxCap = SUB_ROWS / SUB_PER_PLOT
    hostingCaps.forEach((capRaw, c) => {
        const cap = Math.min(capRaw ?? 0, maxCap)
        if (cap <= 0) return
        for (const side of SIDES) {
            const { x, y, w, h, cx } = laneBox(side, c, cap)
            // An ARMED self lane wears the attack red instead of the airspace blue;
            // a lane a drone is being dragged into wears the amber edge.
            const armed = side === 'self' && armedCols.has(c)
            const hover = side === 'self' && hoverCol === c
            const flashing = side === 'self' && flash?.col === c
            lanes.push(
                <g key={`${side}-${c}`} opacity={side === 'opp' ? LANE_OPP_OPACITY : 1} data-lane={`${side}-${c}`}
                    data-aiming={side === 'opp' && aimingCols?.has(c) ? 'true' : undefined}
                    data-hover={hover ? 'true' : undefined} data-flash={flashing ? 'true' : undefined}>
                    <rect x={x} y={y} width={w} height={h} rx={w / 2}
                        fill={`url(#hexb-lane-haze-${armed ? 'armed' : side})`} filter="url(#hexb-lane-feather)" />
                    {/* The landing blink: fill AND edge pop amber together, then fade. */}
                    {flashing && (
                        <g key={flash.key} className="hexb__lane-flash">
                            <rect x={x} y={y} width={w} height={h} rx={w / 2} fill={LANE_FLASH_FILL} />
                            <rect x={x} y={y} width={w} height={h} rx={w / 2} fill="none"
                                stroke={LANE_FLASH_STROKE} strokeWidth={LANE_STROKE_W + 0.5} strokeLinejoin="round" />
                        </g>
                    )}
                    <rect x={x} y={y} width={w} height={h} rx={w / 2}
                        fill="none" stroke={hover ? LANE_STROKE_HOVER : armed ? LANE_STROKE_ARMED : LANE_STROKE}
                        strokeWidth={LANE_STROKE_W} strokeLinejoin="round" strokeLinecap="round" />
                    {side === 'self' && spentMoves?.get(c) && spentMoveMark(c, x, y, cx, spentMoves.get(c))}
                </g>
            )
            if (side !== 'self' || !(armed || eligibleCols.has(c))) continue

            // The attack caret: the self lane's point at its band-end cap.
            const lift = caretLift?.col === c ? Math.max(-CARET_LIFT_MAX, Math.min(CARET_LIFT_MAX, caretLift.dy)) : 0
            // Base corners lie ON the cap's arc: the cap circle is centred
            // LANE_RADIUS below the pill's top, so at ±CARET_HALF_W the arc
            // sits sqrt(r² − w²) above that centre.
            const arcCy = y + LANE_RADIUS
            const baseY = arcCy - Math.sqrt(LANE_RADIUS * LANE_RADIUS - CARET_HALF_W * CARET_HALF_W)
            const apexY = y - CARET_H
            const caretPath = `M ${cx - CARET_HALF_W} ${baseY} L ${cx} ${apexY} L ${cx + CARET_HALF_W} ${baseY} `
                + `A ${LANE_RADIUS} ${LANE_RADIUS} 0 0 0 ${cx - CARET_HALF_W} ${baseY} Z`
            // Chevrons share the caret's slope (its sides' rise over CARET_HALF_W).
            const rise = baseY - apexY
            const chevronFade = 1 - Math.min(1, Math.max(0, -lift) / CARET_LIFT_MAX)
            const chevrons = armed ? null : CHEVRON_OPACITY.map((op, k) => {
                const ay = apexY - (k + 1) * CHEVRON_STEP
                const pts = `${cx - CARET_HALF_W},${ay + rise} ${cx},${ay} ${cx + CARET_HALF_W},${ay + rise}`
                return (
                    <g key={k} opacity={op * chevronFade}>
                        <polyline points={pts} fill="none" stroke="#000" strokeOpacity="0.7"
                            strokeWidth={CHEVRON_HALO_W} strokeLinecap="round" strokeLinejoin="round"
                            filter="url(#hexb-caret-shadow)" />
                        <polyline points={pts} fill="none" stroke={CARET_FILL}
                            strokeWidth={CHEVRON_W} strokeLinecap="round" strokeLinejoin="round" />
                    </g>
                )
            })
            // The caret's shadow falls NORTH (into the band, away from the pill)
            // and is drawn in its own layer beneath the lanes, so the pill always
            // covers it — the point casts onto the band, never onto its own airspace.
            caretShadows.push(
                <path key={c} d={caretPath} transform={`translate(0 ${lift - CARET_SHADOW_DY})`}
                    fill="#000" opacity="0.6" filter="url(#hexb-caret-shadow)" />
            )
            const hd = caretHandlers
            carets.push(
                <g key={c} transform={`translate(0 ${lift})`} data-caret={c} data-armed={armed ? 'true' : 'false'}
                    // A cursor only ever shows for a mouse, and a mouse CLICKS the
                    // caret (the drag is touch's): the pointer hand, never a resize.
                    style={{ pointerEvents: interactive ? 'auto' : 'none', cursor: interactive ? 'pointer' : 'default', touchAction: 'none' }}
                    onPointerDown={interactive ? (e) => hd.onDown(e, c) : undefined}
                    onPointerMove={interactive ? hd.onMove : undefined}
                    onPointerUp={interactive ? hd.onUp : undefined}
                    onPointerCancel={interactive ? hd.onUp : undefined}
                    onClick={(e) => e.stopPropagation()}
                >
                    <path d={caretPath} fill={CARET_FILL} stroke={CARET_FILL} strokeWidth="1" strokeLinejoin="round" />
                    {chevrons}
                    {/* Finger-sized hit target around the point. */}
                    <circle cx={cx} cy={y - 8} r={18} fill="transparent" />
                </g>
            )
        }
    })
    return { lanes, carets, caretShadows }
}
